import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ProvisionDetailModal } from "../components/provisions/ProvisionDetailModal";
import { InstallModal } from "../components/provisions/InstallModal";
import { RollbackModal } from "../components/provisions/RollbackModal";
import { CancelModal } from "../components/provisions/CancelModal";
import { ProvisionStats } from "../components/provisions/ProvisionStats";
import { ProvisionsTable } from "../components/provisions/ProvisionsTable";
import { Button } from "../components/ui/button";
import { Wrench, Download } from "lucide-react";
import { downloadCSV, csvDate } from "../utils/csv";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  PENDIENTE_COMERCIAL: "Pendiente Comercial",
  PENDIENTE_ADMINISTRATIVA: "Pendiente Administrativa",
  PENDIENTE_TECNICA: "Pendiente Técnica",
  COMPLETADA: "Completada",
  CANCELADA: "Cancelada",
};

const statusColors = {
  PENDIENTE_COMERCIAL: "bg-blue-500",
  PENDIENTE_ADMINISTRATIVA: "bg-yellow-500",
  PENDIENTE_TECNICA: "bg-orange-500",
  COMPLETADA: "bg-green-500",
  CANCELADA: "bg-red-500",
};

const ticketStatusLabels = {
  BLOQUEADO: "Bloqueado",
  PENDIENTE: "Pendiente",
  EN_PROGRESO: "En Progreso",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

const ticketStatusColors = {
  BLOQUEADO: "bg-gray-500",
  PENDIENTE: "bg-yellow-500",
  EN_PROGRESO: "bg-blue-500",
  COMPLETADO: "bg-green-500",
  CANCELADO: "bg-red-500",
};

const ProvisionsPage = () => {
  const { user, hasPermission, canDelete, canEdit, canExport } = useAuth();
  // Edit gate — backend mirror. A user with view + assignment can open the
  // detail (and thus reach this dialog) but their controls must stay
  // read-only unless their permission matrix grants `provisions.edit`.
  const canEditProvisions = canEdit("provisions");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [provisions, setProvisions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProvision, setSelectedProvision] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("comercial");
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackReason, setRollbackReason] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [provisionToDelete, setProvisionToDelete] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [installItemId, setInstallItemId] = useState(null);
  const [installDate, setInstallDate] = useState("");

  const fetchProvisions = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      
      const response = await axios.get(`${API_URL}/api/provisions`, { params });
      setProvisions(response.data);
    } catch (error) {
      console.error("Error fetching provisions:", error);
      toast.error("Error al cargar provisiones");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/provisions/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchProvisions();
    fetchStats();
    fetchUsers();
  }, [fetchProvisions]);

  // Deep-link: open specific provision detail when ?open=<id> is present
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || provisions.length === 0 || detailModalOpen) return;
    const target = provisions.find((p) => p.id === openId);
    if (target) {
      openDetailModal(target);
      navigate("/provisions", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, provisions]);

  const openDetailModal = async (provision) => {
    setSelectedProvision(provision);
    setDetailModalOpen(true);
    setExpandedItems({});
    
    // Set active tab based on status
    if (provision.status === "PENDIENTE_COMERCIAL") {
      setActiveTab("comercial");
    } else if (provision.status === "PENDIENTE_ADMINISTRATIVA") {
      setActiveTab("administrativa");
    } else {
      setActiveTab("tecnica");
    }
  };

  const handleChecklistUpdate = async (phase, taskIndex, completed, comments = null) => {
    if (!selectedProvision) return;
    
    const checklistKey = `${phase}_checklist`;
    const updatedChecklist = [...selectedProvision[checklistKey]];
    updatedChecklist[taskIndex] = {
      ...updatedChecklist[taskIndex],
      completed,
      comments: comments !== null ? comments : updatedChecklist[taskIndex].comments,
      completed_at: completed ? new Date().toISOString() : null,
      completed_by: completed ? user?.id : null,
      completed_by_name: completed ? user?.name : null,
    };
    
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, {
        [checklistKey]: updatedChecklist
      });
      
      // Refresh provision data
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
      fetchStats();
      toast.success("Checklist actualizado");
    } catch (error) {
      console.error("Error updating checklist:", error);
      toast.error("Error al actualizar checklist");
    }
  };

  const handleAssignmentUpdate = async (field, value) => {
    if (!selectedProvision) return;
    
    const updateData = { [field]: value };
    
    // Get name if assigning user
    if (field.endsWith("_id") && value) {
      const assignedUser = users.find(u => u.id === value);
      const nameField = field.replace("_id", "_name");
      updateData[nameField] = assignedUser?.name || "";
    }
    
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, updateData);
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
      toast.success("Asignación actualizada");
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Error al actualizar asignación");
    }
  };

  const handleRollback = async () => {
    if (!selectedProvision || !rollbackReason.trim()) {
      toast.error("El motivo de retroceso es obligatorio");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/provisions/${selectedProvision.id}/rollback`, {
        reason: rollbackReason
      });
      toast.success("Provisión retrocedida a la fase anterior");
      setRollbackModalOpen(false);
      setRollbackReason("");
      
      // Refresh provision
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
      fetchStats();
    } catch (error) {
      console.error("Error rolling back provision:", error);
      toast.error(error.response?.data?.detail || "Error al retroceder provisión");
    }
  };

  // Helper: Determine if a tab can be edited based on sequential flow AND user role
  const canEditTab = (tabName) => {
    if (!selectedProvision) return false;
    // Hard gate: regardless of role/phase, no edit without provisions.edit
    // permission. View-only assigned users still see the tab but read-only.
    if (!canEditProvisions) return false;

    const status = selectedProvision.status;
    const roles = user?.roles || [];
    
    // SUPERADMIN can edit any phase that is current
    if (roles.includes("SUPERADMIN")) {
      if (tabName === "comercial") return status === "PENDIENTE_COMERCIAL";
      if (tabName === "administrativa") return status === "PENDIENTE_ADMINISTRATIVA";
      if (tabName === "tecnica") return status === "PENDIENTE_TECNICA";
      return false;
    }
    
    // Each role can ONLY edit their specific phase when it's current
    if (tabName === "comercial") {
      return roles.includes("COMERCIAL") && status === "PENDIENTE_COMERCIAL";
    }
    if (tabName === "administrativa") {
      return roles.includes("GESTION") && status === "PENDIENTE_ADMINISTRATIVA";
    }
    if (tabName === "tecnica") {
      return roles.includes("TECNICO") && status === "PENDIENTE_TECNICA";
    }
    return false;
  };

  const canAdvance = () => {
    if (!selectedProvision) return false;
    // Only allow advance from Comercial and Administrativa phases
    // Check if current phase checklist is complete before advancing
    if (selectedProvision.status === "PENDIENTE_COMERCIAL") {
      return selectedProvision.comercial_progress === 100;
    }
    if (selectedProvision.status === "PENDIENTE_ADMINISTRATIVA") {
      return selectedProvision.administrativa_progress === 100;
    }
    // No advance button in Técnica phase (provisión se completa automáticamente)
    return false;
  };

  // Toggle all checklist items for a phase
  const handleToggleAllChecklist = async (phase, selectAll) => {
    if (!selectedProvision) return;
    
    const checklistKey = `${phase}_checklist`;
    const currentChecklist = selectedProvision[checklistKey] || [];
    
    const updatedChecklist = currentChecklist.map(item => ({
      ...item,
      completed: selectAll,
      completed_at: selectAll ? new Date().toISOString() : null,
      completed_by: selectAll ? user?.id : null,
      completed_by_name: selectAll ? user?.name : null,
    }));
    
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, {
        [checklistKey]: updatedChecklist
      });
      
      // Refresh provision data
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
      fetchStats();
      toast.success(selectAll ? "Todas las tareas marcadas" : "Todas las tareas desmarcadas");
    } catch (error) {
      console.error("Error updating checklist:", error);
      toast.error("Error al actualizar checklist");
    }
  };

  // Handle technical item checklist toggle
  const handleTechnicalItemChecklistUpdate = async (itemId, taskId, checked) => {
    if (!selectedProvision) return;
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}/technical-items/${itemId}/checklist`, {
        task_id: taskId, completed: checked,
      });
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      toast.success("Tarea actualizada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar tarea");
    }
  };

  // Install a technical item
  const handleInstallItem = async () => {
    if (!selectedProvision || !installItemId || !installDate) return;
    try {
      await axios.post(`${API_URL}/api/provisions/${selectedProvision.id}/technical-items/${installItemId}/install`, {
        fecha_instalacion: installDate,
      });
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      setInstallModalOpen(false);
      setInstallItemId(null);
      setInstallDate("");
      toast.success("Servicio marcado como instalado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al instalar");
    }
  };

  // Complete provision (all items must be INSTALADO)
  const handleCompleteProvision = async () => {
    if (!selectedProvision) return;
    try {
      await axios.post(`${API_URL}/api/provisions/${selectedProvision.id}/complete`);
      toast.success("Provisión completada exitosamente");
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar provisión");
    }
  };

  const toggleItemExpanded = (itemId) => setExpandedItems(p => ({ ...p, [itemId]: !p[itemId] }));

  // Cancel provision
  const handleCancelProvision = async () => {
    if (!selectedProvision || !cancelReason.trim()) {
      toast.error("El motivo de cancelación es obligatorio");
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, {
        status: "CANCELADA",
        cancel_reason: cancelReason,
        cancelled_at: new Date().toISOString(),
        cancelled_by: user?.id,
        cancelled_by_name: user?.name
      });
      toast.success("Provisión cancelada");
      setCancelModalOpen(false);
      setCancelReason("");
      setDetailModalOpen(false);
      fetchProvisions();
      fetchStats();
    } catch (error) {
      console.error("Error cancelling provision:", error);
      toast.error("Error al cancelar provisión");
    }
  };

  const handleDeleteProvision = async () => {
    if (!provisionToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/provisions/${provisionToDelete.id}`);
      toast.success("Provisión eliminada correctamente");
      setDeleteDialogOpen(false);
      setProvisionToDelete(null);
      setDetailModalOpen(false);
      fetchProvisions();
      fetchStats();
    } catch (error) {
      console.error("Error deleting provision:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar provisión");
    }
  };

  const openDeleteDialog = (provision) => {
    setProvisionToDelete(provision);
    setDeleteDialogOpen(true);
  };

  const handleAdvancePhase = async () => {
    if (!selectedProvision) return;
    
    const nextStatus = {
      "PENDIENTE_COMERCIAL": "PENDIENTE_ADMINISTRATIVA",
      "PENDIENTE_ADMINISTRATIVA": "PENDIENTE_TECNICA"
    };
    
    const nextTab = {
      "PENDIENTE_COMERCIAL": "administrativa",
      "PENDIENTE_ADMINISTRATIVA": "tecnica"
    };
    
    const newStatus = nextStatus[selectedProvision.status];
    if (!newStatus) return;
    
    try {
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, {
        status: newStatus
      });
      toast.success(`Provisión avanzada a ${statusLabels[newStatus] || newStatus}`);
      
      // Refresh selected provision
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      
      // Auto-switch to the new phase tab
      const newTab = nextTab[selectedProvision.status];
      if (newTab) {
        setActiveTab(newTab);
      }
      
      fetchProvisions();
      fetchStats();
    } catch (error) {
      console.error("Error advancing provision:", error);
      toast.error(error.response?.data?.detail || "Error al avanzar provisión");
    }
  };

  const handleAssignTechnician = async (technicianId) => {
    if (!selectedProvision) return;
    
    try {
      const technician = technicianId ? tecnicos.find(t => t.id === technicianId) : null;
      await axios.put(`${API_URL}/api/provisions/${selectedProvision.id}`, {
        assigned_technician_id: technicianId || null,
        assigned_technician_name: technician?.name || ""
      });
      toast.success(technicianId ? "Técnico asignado correctamente" : "Técnico desasignado");
      fetchProvisions();
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error("Error al asignar técnico");
    }
  };

  const handleRefreshChecklist = async () => {
    if (!selectedProvision) return;
    
    try {
      await axios.post(`${API_URL}/api/provisions/${selectedProvision.id}/refresh-checklist`);
      toast.success("Checklist actualizado con las tareas del maestro");
      
      // Refresh provision
      const response = await axios.get(`${API_URL}/api/provisions/${selectedProvision.id}`);
      setSelectedProvision(response.data);
      fetchProvisions();
    } catch (error) {
      console.error("Error refreshing checklist:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar checklist");
    }
  };

  const filteredProvisions = provisions.filter(p => {
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        p.customer_name?.toLowerCase().includes(search) ||
        p.provision_id?.toLowerCase().includes(search) ||
        p.location_name?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Filter by "Mis Asignadas"
    if (statusFilter === "MIS_ASIGNADAS") {
      return p.comercial_id === user?.id || 
             p.administrativa_id === user?.id || 
             p.tecnico_id === user?.id;
    }
    
    return true;
  });

  // Calculate stats including "Mis asignadas"
  const myAssignedCount = provisions.filter(p => 
    p.comercial_id === user?.id || 
    p.administrativa_id === user?.id || 
    p.tecnico_id === user?.id
  ).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const comerciales = users.filter(u => u.roles?.includes("COMERCIAL"));
  const gestores = users.filter(u => u.roles?.includes("GESTION"));
  const tecnicos = users.filter(u => u.roles?.includes("TECNICO"));

  const handleExportCSV = () => {
    if (filteredProvisions.length === 0) {
      toast.error("No hay provisiones para exportar");
      return;
    }
    const columns = [
      { key: "provision_id", label: "Referencia" },
      { key: "customer_name", label: "Cliente" },
      { key: "location_name", label: "Ubicación" },
      { key: "status", label: "Estado", format: (v) => statusLabels[v] || v || "" },
      { key: "tecnico_nombre", label: "Técnico" },
      { key: "scheduled_date", label: "Fecha programada", format: csvDate },
      { key: "created_at", label: "Creada", format: csvDate },
    ];
    downloadCSV("provisiones", columns, filteredProvisions);
    toast.success(`${filteredProvisions.length} provisiones exportadas a CSV`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="provisions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Wrench className="h-8 w-8 text-[#03BEE4]" />
            Provisión de Servicios
          </h1>
          <p className="text-slate-400 mt-1">
            Gestiona el proceso de alta de nuevos servicios
          </p>
        </div>
        {canExport("provisions") && (
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
            data-testid="export-provisions-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        )}
      </div>

      <ProvisionStats
        stats={stats}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        myAssignedCount={myAssignedCount}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Provisions Table */}
      <ProvisionsTable
        filteredProvisions={filteredProvisions}
        statusColors={statusColors}
        statusLabels={statusLabels}
        formatDate={formatDate}
        openDetailModal={openDetailModal}
        canDelete={canDelete}
        openDeleteDialog={openDeleteDialog}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        provisionToDelete={provisionToDelete}
        handleDeleteProvision={handleDeleteProvision}
      />

      {/* Detail Modal */}
      <ProvisionDetailModal
        detailModalOpen={detailModalOpen}
        setDetailModalOpen={setDetailModalOpen}
        selectedProvision={selectedProvision}
        setSelectedProvision={setSelectedProvision}
        statusColors={statusColors}
        statusLabels={statusLabels}
        formatDate={formatDate}
        canEditProvisions={canEditProvisions}
        comerciales={comerciales}
        gestores={gestores}
        tecnicos={tecnicos}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        expandedItems={expandedItems}
        toggleItemExpanded={toggleItemExpanded}
        canEditTab={canEditTab}
        canAdvance={canAdvance}
        setRollbackModalOpen={setRollbackModalOpen}
        setCancelModalOpen={setCancelModalOpen}
        setInstallItemId={setInstallItemId}
        setInstallDate={setInstallDate}
        setInstallModalOpen={setInstallModalOpen}
        handleAdvancePhase={handleAdvancePhase}
        handleAssignmentUpdate={handleAssignmentUpdate}
        handleAssignTechnician={handleAssignTechnician}
        handleRefreshChecklist={handleRefreshChecklist}
        handleChecklistUpdate={handleChecklistUpdate}
        handleToggleAllChecklist={handleToggleAllChecklist}
        handleTechnicalItemChecklistUpdate={handleTechnicalItemChecklistUpdate}
        handleCompleteProvision={handleCompleteProvision}
      />

      {/* Install Modal */}
      <InstallModal
        installModalOpen={installModalOpen}
        setInstallModalOpen={setInstallModalOpen}
        installDate={installDate}
        setInstallDate={setInstallDate}
        handleInstallItem={handleInstallItem}
      />

      {/* Rollback Modal */}
      <RollbackModal
        rollbackModalOpen={rollbackModalOpen}
        setRollbackModalOpen={setRollbackModalOpen}
        rollbackReason={rollbackReason}
        setRollbackReason={setRollbackReason}
        handleRollback={handleRollback}
      />

      {/* Cancel Provision Modal */}
      <CancelModal
        cancelModalOpen={cancelModalOpen}
        setCancelModalOpen={setCancelModalOpen}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        handleCancelProvision={handleCancelProvision}
      />
    </div>
  );
};

export default ProvisionsPage;
